import {ApiParams, ApiStatus, IWorkspace, IWorkspaceConfig, IWorkspaceStats} from '../../models';
import {WorkspacesAction, WorkspacesActionTypes} from "../actions/workspaceActions";
import {WorkspaceConfigActions, WorkspaceConfigActionTypes} from '../actions/workspaceConfigActions';
import {ISavedOrganizationAction, OrganizationsActionTypes} from '../actions/organizationActions';
import config from '../../../config';

export const initialWorkspaceState: IWorkspaceState = {
  loadingListStatus: ApiStatus.LOADING,
  loadingStatus: ApiStatus.LOADING,
  addingStatus: ApiStatus.LOADED,
  savingStatus: ApiStatus.LOADED,
  switchingStatus: ApiStatus.LOADED,
  updateWorkspaceConfigStatus: null,
  loadCurrentWorkspaceConfigStatus: null,
  currentWorkspaceConfig: null,
  workspaces: [],
  workspacesParams: {},
  workspace: null, // current
  loadingStatsStatus: ApiStatus.LOADING,
  stats: {
    integrations_count: 0,
    tableau_integrations_count: 0,
    databases_count: 0,
    workflows_count: 0,
    users_count: 0,
    reports_count: 0,
  },
}

export interface IWorkspaceState {
  loadingListStatus: ApiStatus;
  loadingStatus: ApiStatus;
  addingStatus: ApiStatus;
  savingStatus: ApiStatus;
  switchingStatus: ApiStatus;
  updateWorkspaceConfigStatus: ApiStatus | null;
  loadCurrentWorkspaceConfigStatus: ApiStatus | null;
  currentWorkspaceConfig: IWorkspaceConfig[] | null;
  workspaces: IWorkspace[];
  workspacesParams: ApiParams | undefined;
  workspace: IWorkspace | null;
  loadingStatsStatus: ApiStatus;
  stats: IWorkspaceStats;
}

export default function workspacesReducer(
  state: IWorkspaceState = initialWorkspaceState,
  action: WorkspacesAction | WorkspaceConfigActions | ISavedOrganizationAction
): IWorkspaceState {
  switch (action.type) {
    case WorkspacesActionTypes.LOAD_WORKSPACES:
    case WorkspacesActionTypes.LOADING_WORKSPACES:
      return {
        ...state,
        loadingListStatus: ApiStatus.LOADING
      };

    case WorkspacesActionTypes.LOADING_WORKSPACES_FAILED:
      return {
        ...state,
        loadingListStatus: ApiStatus.FAILED
      };

    case WorkspacesActionTypes.LOADED_WORKSPACES:
      return {
        ...state,
        loadingListStatus: ApiStatus.LOADED,
        workspaces: action.payload.workspaces
      };

    case WorkspacesActionTypes.LOAD_WORKSPACE:
    case WorkspacesActionTypes.LOADING_WORKSPACE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING
      };

    case WorkspacesActionTypes.LOAD_WORKSPACE_STATS:
    case WorkspacesActionTypes.LOADING_WORKSPACE_STATS:
      return {
        ...state,
        loadingStatsStatus: ApiStatus.LOADING
      };

    case WorkspacesActionTypes.LOADING_WORKSPACE_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED
      };

    case WorkspacesActionTypes.LOADING_WORKSPACE_STATS_FAILED:
      return {
        ...state,
        loadingStatsStatus: ApiStatus.FAILED
      };

    case WorkspacesActionTypes.LOADED_WORKSPACE_STATS:
      return {
        ...state,
        loadingStatsStatus: ApiStatus.LOADED,
        stats: action.payload.stats
      };

    case WorkspacesActionTypes.LOADED_WORKSPACE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        workspace: action.payload.workspace,
        workspaces: state.workspaces.map(
          workspace => workspace.uid === action.payload.workspace.uid ?
            {
              ...action.payload.workspace,
              is_active: true
            } : {
              ...workspace,
              is_active: false
            }
        )
      };

    case WorkspacesActionTypes.SAVE_WORKSPACE:
    case WorkspacesActionTypes.SAVING_WORKSPACE:
      return {
        ...state,
        savingStatus: ApiStatus.LOADING
      };

    case WorkspacesActionTypes.SAVING_WORKSPACE_FAILED:
      return {
        ...state,
        savingStatus: ApiStatus.FAILED
      };

    case WorkspacesActionTypes.SWITCH_WORKSPACE:
    case WorkspacesActionTypes.SWITCHING_WORKSPACE:
      return {
        ...state,
        switchingStatus: ApiStatus.LOADING
      };

    case WorkspacesActionTypes.SWITCH_WORKSPACE_ERROR:
      return {
        ...state,
        switchingStatus: ApiStatus.FAILED
      };

    case WorkspacesActionTypes.SWITCHED_WORKSPACE:
      setTimeout(() => {
        window.location.href = '/app';
      }, 100);

      return {
        ...state,
        loadingStatus: ApiStatus.LOADED
      }

    case WorkspacesActionTypes.SAVED_WORKSPACE:
      return {
        ...state,
        savingStatus: ApiStatus.LOADED,
        workspace: {
          ...state.workspace,
          ...action.payload.workspace
        }
      };

    case WorkspaceConfigActionTypes.UPDATE_WORKSPACE_CONFIG:
      return {
        ...state,
        updateWorkspaceConfigStatus: ApiStatus.LOADING,
      }
    case WorkspaceConfigActionTypes.UPDATING_WORKSPACE_CONFIG_FAILED:
      return {
        ...state,
        updateWorkspaceConfigStatus: ApiStatus.FAILED,
      }

    case WorkspaceConfigActionTypes.UPDATED_WORKSPACE_CONFIG:
      const configJsonName = action.payload.name;
      const configJsonValue = action.payload.value;

      return {
        ...state,
        updateWorkspaceConfigStatus: ApiStatus.LOADED,
        workspace: state.workspace ? {
          ...state.workspace,
          config_json: {
            ...state.workspace?.config_json,
            [configJsonName]: configJsonValue,
          }
        } : state.workspace
      }

    case OrganizationsActionTypes.SAVED_ORGANIZATION:
      // Update current workspace organization in case organization name changed
      return {
        ...state,
        workspace: state.workspace ? {
          ...state.workspace,
          organization: {
            ...state.workspace.organization,
            ...action.payload.organization,
          }
        } : state.workspace,
      }

    case WorkspaceConfigActionTypes.LOAD_CURRENT_WORKSPACE_CONFIG:
    case WorkspaceConfigActionTypes.LOADING_CURRENT_WORKSPACE_CONFIG:
      return {
        ...state,
        loadCurrentWorkspaceConfigStatus: ApiStatus.LOADING,
      }

    case WorkspaceConfigActionTypes.LOADED_CURRENT_WORKSPACE_CONFIG:
      return {
        ...state,
        loadCurrentWorkspaceConfigStatus: ApiStatus.LOADED,
        currentWorkspaceConfig: action.payload,
      }

    case WorkspaceConfigActionTypes.LOADING_CURRENT_WORKSPACE_CONFIG_FAILED:
      return {
        ...state,
        loadCurrentWorkspaceConfigStatus: ApiStatus.FAILED,
      }

    default:
      return state;
  }
}
